import React from 'react';
import { Box, Grid } from '@mui/material';
import Shimmer from 'ui-components/Shimmer';

// Placeholders for Performance.js. Numbers come from MetricSummaryCard.js,
// MetricChartCard.js and the scoped table styles on #monthly-performance-table.
const CARD_BORDER = '#EAECF0';
const HEADER_BG = '#F5F3FF';

// Column count is not known while savedConfigurationData is in flight.
const WIDGETS = 4;
const TABLE_COLUMNS = 6;
const TABLE_ROWS = 8;
const CHARTS = 3;

const cardSx = {
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: '12px',
  background: '#fff',
  height: '100%',
};

// MetricSummaryCard variant="chart": icon + label over the value, sparkline right.
const WidgetSkeleton = () => (
  <Box sx={{ ...cardSx, padding: { xs: '0.875rem 1rem', md: '1rem 1.25rem' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Shimmer width={30} height={30} radius={8} />
        <Shimmer width="55%" height={12} radius={4} />
      </Box>
      <Shimmer width="70%" height={22} radius={4} />
    </Box>
    {/* The sparkline is 52px tall and 62-92px wide depending on breakpoint. */}
    <Shimmer width={82} height={52} radius={6} sx={{ flexShrink: 0 }} />
  </Box>
);

export const PerformanceWidgetsSkeleton = ({ count = WIDGETS }) => (
  <Grid container spacing={{ xs: 1.25, sm: 2 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid item xs={12} sm={6} md={6} lg={3} key={i}>
        <WidgetSkeleton />
      </Grid>
    ))}
  </Grid>
);

// The Monthly Performance card: title, then a table whose header is #F5F3FF and
// whose body cells stack a value over a TrendIndicator.
export const PerformanceTableSkeleton = () => (
  <Box sx={{ ...cardSx, borderRadius: { xs: '12px', md: '16px' }, padding: { xs: '0.75rem', md: '1.25rem' } }}>
    <Shimmer width={190} height={17} radius={4} sx={{ mb: { xs: 1, md: 1.25 } }} />

    <Box sx={{ width: '100%', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      <Box sx={{ minWidth: '100%', width: 'max-content', borderRadius: '10px' }}>
        <Box sx={{ display: 'flex', background: HEADER_BG, borderRadius: '10px', padding: { xs: '0.5rem', md: '0.75rem 1rem' } }}>
          {Array.from({ length: TABLE_COLUMNS }).map((_, i) => (
            <Box key={i} sx={{ minWidth: i === 0 ? 84 : 120, flex: '1 1 auto', display: 'flex', alignItems: 'center' }}>
              <Shimmer width={i === 0 ? 48 : 78} height={11} radius={4} />
            </Box>
          ))}
        </Box>

        {Array.from({ length: TABLE_ROWS }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              borderBottom: rowIndex === TABLE_ROWS - 1 ? 'none' : '1px solid #F2F4F7',
              padding: { xs: '0.6rem 0.5rem', md: '0.9rem 1rem' },
            }}
          >
            {Array.from({ length: TABLE_COLUMNS }).map((_, colIndex) => (
              <Box key={colIndex} sx={{ minWidth: colIndex === 0 ? 84 : 120, flex: '1 1 auto' }}>
                <Shimmer width={colIndex === 0 ? 62 : 54} height={13} radius={4} />
                {/* Metric cells carry a TrendIndicator under the value. */}
                {colIndex > 0 && <Shimmer width={38} height={9} radius={4} sx={{ mt: '4px' }} />}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

// MetricChartCard: title + chip, then a 240px plot.
const ChartCardSkeleton = () => (
  <Box sx={{ ...cardSx, padding: '1rem 1.25rem' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
      <Shimmer width="45%" height={14} radius={4} />
      <Shimmer width={56} height={20} radius={6} />
    </Box>
    <Shimmer width="100%" height={240} radius={8} />
  </Box>
);

export const PerformanceGraphsSkeleton = () => (
  <Grid container xs={12} maxWidth={"100%"} spacing={2}>
    <Grid item xs={12}>
      <PerformanceWidgetsSkeleton />
    </Grid>
    {Array.from({ length: CHARTS }).map((_, i) => (
      <Grid item xs={12} sm={6} lg={4} key={i}>
        <ChartCardSkeleton />
      </Grid>
    ))}
  </Grid>
);

export const PerformanceDataSkeleton = () => (
  <Grid container xs={12} spacing={2}>
    <Grid item xs={12}>
      <PerformanceWidgetsSkeleton />
    </Grid>
    <Grid item xs={12}>
      <PerformanceTableSkeleton />
    </Grid>
  </Grid>
);

// Whole-tab placeholder: the tab strip and its filter row, then the Data view.
const PerformanceSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        rowGap: 1,
        borderBottom: '1px solid #EAECF0',
        paddingBlock: '0.5rem',
        paddingInline: { xs: '0.5rem', sm: '1rem' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, paddingInline: { xs: 0, sm: '2rem' }, minHeight: 48, alignItems: 'center' }}>
        <Shimmer width={44} height={14} radius={4} />
        <Shimmer width={58} height={14} radius={4} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Shimmer width={230} height={40} radius={8} />
        <Shimmer width={32} height={32} radius={4} />
      </Box>
    </Box>

    <Box sx={{ padding: { xs: '0.5rem', md: '1rem' } }}>
      <PerformanceDataSkeleton />
    </Box>
  </Box>
);

export default PerformanceSkeleton;
